import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/authApi';
import { 
  storeTokens, 
  getStoredAuth, 
  getStoredRefreshToken,
  clearStoredToken, 
  isTokenExpired,
  updateStoredToken,
  updateStoredRefreshToken
} from '../utils/auth';
import { IUser } from '../types/user';
import { 
  ILoginCredentials, 
  IRegisterCredentials,
  IAppleLoginCredentials,
  SocialAuthResult,
  IPendingSocialAuth,
  SocialAuthCompletion
} from '../types/auth';

interface AuthContextValue {
  user: IUser | null;
  loading: boolean;
  error: string | null;
  authenticated: boolean;
  isAuthenticated: () => boolean;
  getUser: () => IUser | null;
  getCurrentUsername: () => string | null;
  getCurrentUserId: () => string | null;
  login: (credentials: ILoginCredentials) => Promise<void>;
  googleLogin: (credential: string) => Promise<IPendingSocialAuth | void>;
  appleLogin: (credentials: IAppleLoginCredentials) => Promise<IPendingSocialAuth | void>;
  completeSocialLogin: (payload: SocialAuthCompletion) => Promise<void>;
  register: (credentials: IRegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (userData: Partial<IUser>) => Promise<void>;
  refreshUser: () => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>;
  refreshAuthToken: () => Promise<void>;
  deleteAccount: (payload?: { password?: string; confirm?: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

const TOKEN_REFRESH_THRESHOLD = 5 * 60; // 5 minutes in seconds

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTimer, setRefreshTimer] = useState<NodeJS.Timeout | null>(null);
  const initializedRef = useRef(false);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Handle authentication error
  const handleAuthError = useCallback((error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    setError(errorMessage);
    setLoading(false);
  }, []);

  // Simplified auth initialization to prevent infinite loops
  const initializeAuth = useCallback(async () => {
    if (initializedRef.current) {
      return;
    }
    
    try {
      setLoading(true);
      initializedRef.current = true; // Set this early to prevent loops
      
      const auth = getStoredAuth();
      
      // If access token is expired but we have a refresh token, try refreshing
      if ((!auth || isTokenExpired()) && getStoredRefreshToken()) {
        try {
          const refreshResponse = await authApi.refreshToken();
          const expiresIn = Math.floor((refreshResponse.expiresAt - Date.now()) / 1000);
          updateStoredToken(refreshResponse.token, expiresIn);
          if (refreshResponse.refreshToken) {
            updateStoredRefreshToken(refreshResponse.refreshToken);
          }
        } catch {
          // Refresh failed — user needs to log in again
          clearStoredToken();
          setUser(null);
          setLoading(false);
          return;
        }
      } else if (!auth || isTokenExpired()) {
        // No token and no refresh token — user needs to log in
        setUser(null);
        setLoading(false);
        return;
      }

      const userData = await authApi.getCurrentUser();
      setUser(userData);
      
    } catch (error) {
      // Check if it's an authentication error (401)
      const authError = error as any;
      if (authError?.status === 401 || authError?.message?.includes('Not authorized')) {
        clearStoredToken();
        setUser(null);
      } else {
        console.error('Failed to load user:', error);
        clearStoredToken();
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize auth state once
  useEffect(() => {
    initializeAuth();
    
    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [initializeAuth, refreshTimer]); // Include dependencies
  
  // Listen for profile update events to refresh user data
  useEffect(() => {
    const handleProfileUpdated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      // Only refresh if this is the current user's profile
      if (user && customEvent.detail?.userId && customEvent.detail.userId === user.id) {
        try {
          const userData = await authApi.getCurrentUser();
          setUser(userData);
        } catch (error) {
          console.error('Failed to refresh user data after profile update:', error);
        }
      }
    };
    
    const handleSavedPostsUpdated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      // Only refresh if this is the current user
      if (user && customEvent.detail?.userId && customEvent.detail.userId === user.id) {
        try {
          const userData = await authApi.getCurrentUser();
          setUser(userData);
        } catch (error) {
          console.error('Failed to refresh user data after saved posts update:', error);
        }
      }
    };
    
    window.addEventListener('profileUpdated', handleProfileUpdated);
    window.addEventListener('savedPostsUpdated', handleSavedPostsUpdated);
    
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdated);
      window.removeEventListener('savedPostsUpdated', handleSavedPostsUpdated);
    };
  }, [user]);

  // Login handler
  const login = async (credentials: ILoginCredentials): Promise<void> => {
    try {
      setLoading(true);
      clearError();

      const response = await authApi.login(credentials);
      
      
      // authApi.login returns the data portion (IAuthResult), so access properties directly
      storeTokens(response.token, response.refreshToken, response.expiresIn);
      setUser(response.user);
      
      
      // Set up token refresh
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
      
      const timer = setInterval(async () => {
        try {
          const refreshResponse = await authApi.refreshToken();
          // Calculate expiresIn from expiresAt timestamp
          const expiresIn = Math.floor((refreshResponse.expiresAt - Date.now()) / 1000);
          updateStoredToken(refreshResponse.token, expiresIn);
          // Also update the refresh token (token rotation)
          if (refreshResponse.refreshToken) {
            updateStoredRefreshToken(refreshResponse.refreshToken);
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          clearStoredToken();
          setUser(null);
          navigate('/', { replace: true });
        }
      }, TOKEN_REFRESH_THRESHOLD * 1000);
      
      setRefreshTimer(timer);

      // Check if this is a service provider who just verified their email
      const pendingAccountType = localStorage.getItem('pendingAccountType');
      // Defer navigation to the next tick so that any open Dialog/Modal
      // (e.g. AuthModal) can process its onClose and start its exit
      // transition before the route changes. Without this, the MUI Dialog
      // backdrop can get stuck in the DOM and block all interactions.
      if (pendingAccountType === 'service_provider' && response.user.role === 'service_provider') {
        // Clear the pending account type
        localStorage.removeItem('pendingAccountType');
        // Redirect to business setup wizard
        setTimeout(() => navigate('/business/setup', { replace: true }), 0);
      } else {
        // Clear any pending account type
        localStorage.removeItem('pendingAccountType');
        // Redirect to intended page or home
        const intendedPath = location.state?.from?.pathname || '/';
        setTimeout(() => navigate(intendedPath, { replace: true }), 0);
      }
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Keep session storage, refresh, and navigation identical for social providers.
  const socialLogin = async (request: () => Promise<SocialAuthResult>): Promise<IPendingSocialAuth | void> => {
    try {
      setLoading(true);
      clearError();

      const response = await request();
      // A verified but unlinked identity is not an authenticated session.
      if ('requiresAccountConfirmation' in response) return response;

      storeTokens(response.token, response.refreshToken, response.expiresIn);
      setUser(response.user);

      // Set up token refresh
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }

      const timer = setInterval(async () => {
        try {
          const refreshResponse = await authApi.refreshToken();
          const expiresIn = Math.floor((refreshResponse.expiresAt - Date.now()) / 1000);
          updateStoredToken(refreshResponse.token, expiresIn);
          if (refreshResponse.refreshToken) {
            updateStoredRefreshToken(refreshResponse.refreshToken);
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          clearStoredToken();
          setUser(null);
          navigate('/', { replace: true });
        }
      }, TOKEN_REFRESH_THRESHOLD * 1000);

      setRefreshTimer(timer);

      // Redirect to home – deferred to let Dialog close first (see login())
      const intendedPath = location.state?.from?.pathname || '/';
      setTimeout(() => navigate(intendedPath, { replace: true }), 0);
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = (credential: string): Promise<IPendingSocialAuth | void> =>
    socialLogin(() => authApi.googleLogin(credential));

  const appleLogin = (credentials: IAppleLoginCredentials): Promise<IPendingSocialAuth | void> =>
    socialLogin(() => authApi.appleLogin(credentials));

  const completeSocialLogin = async (payload: SocialAuthCompletion): Promise<void> => {
    await socialLogin(() => authApi.completeSocialLogin(payload));
  };

  // Register handler
  const register = async (credentials: IRegisterCredentials): Promise<void> => {
    try {
      setLoading(true);
      clearError();

      const response = await authApi.register(credentials);
      
      // Check if response includes tokens (immediate login) or requires email verification
      const hasTokens = response && 'token' in response && 'refreshToken' in response;
      
      if (hasTokens) {
        // User can log in immediately (email verification not required)
        storeTokens(response.token, response.refreshToken, response.expiresIn);
        setUser(response.user);
        
        // Set up token refresh
        if (refreshTimer) {
          clearInterval(refreshTimer);
        }
        
        const timer = setInterval(async () => {
          try {
            const refreshResponse = await authApi.refreshToken();
            const expiresIn = Math.floor((refreshResponse.expiresAt - Date.now()) / 1000);
            updateStoredToken(refreshResponse.token, expiresIn);
            // Also update the refresh token (token rotation)
            if (refreshResponse.refreshToken) {
              updateStoredRefreshToken(refreshResponse.refreshToken);
            }
          } catch (error) {
            console.error('Token refresh failed:', error);
            clearStoredToken();
            setUser(null);
            navigate('/', { replace: true });
          }
        }, TOKEN_REFRESH_THRESHOLD * 1000);
        
        setRefreshTimer(timer);

        // Check if user is service provider and needs business setup
        const isServiceProvider = response.user.role === 'service_provider';
        const registeredAsServiceProvider = credentials.accountType === 'service_provider';
        

        if (isServiceProvider && registeredAsServiceProvider) {
          setTimeout(() => navigate('/business/setup', { replace: true }), 0);
        } else {
          setTimeout(() => navigate('/', { replace: true }), 0);
        }
      } else {
        // Registration requires email verification
        // Store the account type for post-verification redirect
        if (credentials.accountType) {
          localStorage.setItem('pendingAccountType', credentials.accountType);
        }
        
        // Redirect to login with success message
        setTimeout(() => navigate('/auth/login', { 
          replace: true, 
          state: { 
            message: 'Registration successful! Please check your email to verify your account.',
            requiresVerification: true,
            accountType: credentials.accountType
          } 
        }), 0);
      }
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout handler
  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
      clearStoredToken();
      setUser(null);
      navigate('/', { replace: true });
    }
  };

  // Delete account handler — permanently deletes the account server-side via
  // the same DELETE /auth/account endpoint the mobile app uses. Unlike
  // logout(), errors are NOT swallowed here: deletion is permanent, so the
  // caller (the confirmation dialog) needs to know about failures such as an
  // incorrect password so it can keep the user on the form instead of
  // clearing their session.
  const deleteAccount = async (payload?: { password?: string; confirm?: boolean }): Promise<void> => {
    const expectedUserId = user?._id || user?.id;
    if (!expectedUserId) throw new Error('Sign in again before deleting your account.');
    await authApi.deleteAccount({ ...payload, expectedUserId });
    if (refreshTimer) {
      clearInterval(refreshTimer);
    }
    clearStoredToken();
    setUser(null);
    navigate('/', { replace: true });
  };

  // Update user data
  const updateUser = async (userData: Partial<IUser>): Promise<void> => {
    try {
      const updatedUser = await authApi.updateUser(userData);
      setUser(updatedUser);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update user data';
      console.error('Failed to update user:', errorMessage);
      throw error;
    }
  };

  // Refresh user data from server
  const refreshUser = async (): Promise<void> => {
    try {
      const userData = await authApi.getCurrentUser();
      setUser(userData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh user data';
      console.error('Failed to refresh user:', errorMessage);
      // Don't throw - silently fail to avoid breaking the UI
    }
  };

  // Email verification
  const verifyEmail = async (token: string): Promise<void> => {
    try {
      setLoading(true);
      clearError();
      await authApi.verifyEmail(token);
      // Verification is public and must never log in the link's account.
      // Refresh only an existing browser session (which may belong to someone
      // else); a failed refresh does not undo successful email verification.
      if (getStoredAuth()) {
        try {
          const userData = await authApi.getCurrentUser();
          setUser(userData);
        } catch {
          // The user can refresh Settings after their connection recovers.
        }
      }
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Request password reset
  const requestPasswordReset = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      clearError();
      await authApi.requestPasswordReset({ email });
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Confirm password reset
  const confirmPasswordReset = async (token: string, newPassword: string): Promise<void> => {
    try {
      setLoading(true);
      clearError();
      await authApi.confirmPasswordReset({ 
        token, 
        password: newPassword, 
        confirmPassword: newPassword 
      });
    } catch (error) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh auth token manually
  const refreshAuthToken = async (): Promise<void> => {
    try {
      const response = await authApi.refreshToken();
      // Calculate expiresIn from expiresAt timestamp
      const expiresIn = Math.floor((response.expiresAt - Date.now()) / 1000);
      updateStoredToken(response.token, expiresIn);
    } catch (error) {
      console.error('Token refresh failed:', error);
      await logout();
      throw error;
    }
  };

  const isAuthenticated = useCallback(() => {
    return !!user && !isTokenExpired();
  }, [user]);

  const getUser = useCallback(() => {
    return user;
  }, [user]);

  const getCurrentUsername = useCallback(() => {
    return user?.username || null;
  }, [user]);

  const getCurrentUserId = useCallback(() => {
    return user?._id || null;
  }, [user]);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    authenticated: !!user,
    isAuthenticated,
    getUser,
    getCurrentUsername,
    getCurrentUserId,
    login,
    googleLogin,
    appleLogin,
    completeSocialLogin,
    register,
    logout,
    clearError,
    updateUser,
    refreshUser,
    verifyEmail,
    requestPasswordReset,
    confirmPasswordReset,
    refreshAuthToken,
    deleteAccount
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
